<template>
    <div>
        <b-row>
            <b-col md="12">
                <editable-toolbar
                    v-if="showHeader"
                    :headerTitle="headerTitle"
                    :headerStyle="headerStyle"
                >
                </editable-toolbar>

                <!-- Selection / Search subbar -->
                <div v-if="showSelectAll || showSearch" class="editable-grid-subbar">
                    <b-row class="no-gutters align-items-center">
                        <b-col v-if="showSelectAll" md="3">
                            <MdCheckBox
                                :Label="$t('Shared.SelectAll')"
                                Name="editableGridSelectAll"
                                ClassProp="AlignCheck mx-3"
                                :CheckedValue="true"
                                :UncheckedValue="false"
                                :value="allRowsSelected"
                                :isReadOnly="d_rows.length === 0"
                                @change="onToggleSelectAll"
                            />
                        </b-col>
                        <b-col v-if="showSelectAll" md="3" class="py-2">
                            <span class="tableSelectedItems">
                                <span class="selItemsVal">{{ d_selected_rows.length }}</span>
                                {{ $t('claimbatchList.item')
                                }}<span v-if="d_selected_rows.length !== 1">s</span>
                                {{ $t('Shared.selected') }}
                            </span>
                        </b-col>
                        <b-col v-if="showSearch" :md="showSelectAll ? 6 : 12">
                            <div class="textlabel" :data-label="$t('Shared.SearchElements')">
                                <div class="input-group">
                                    <input
                                        type="text"
                                        class="form-control"
                                        v-model="d_search_text"
                                        @keyup.enter="onSearch"
                                    />
                                    <div class="input-group-append">
                                        <MdButton
                                            Name="editableGridSearch"
                                            VariantProp="btn btn-sm search-attached"
                                            IconClass="ikon med-zoom"
                                            @click="onSearch"
                                        />
                                    </div>
                                </div>
                            </div>
                        </b-col>
                    </b-row>
                </div>

                <div
                    class="medezTables cutescroll table-responsive"
                    :class="{ 'table-invalid': invalid }"
                >
                    <table
                        role="table"
                        class="table b-table b-table-selectable md-editable-grid"
                        :class="
                            rowSelectionEnabled && multiple
                                ? 'b-table-select-multi'
                                : 'b-table-select-single'
                        "
                        :aria-multiselectable="rowSelectionEnabled && multiple ? 'true' : 'false'"
                        aria-busy="false"
                        :aria-colcount="dataToRender.length"
                        data-pc-section="table"
                    >
                        <editable-table-header
                            :showColumnAction="actions.length !== 0 || rowSelectionEnabled"
                            :frozenColumnAction="frozenColumnAction"
                            :columns="columns.map(x => getColumnHeader(x))"
                        >
                        </editable-table-header>

                        <editable-table-body
                            v-if="d_new_rows.length !== 0"
                            :rows="d_new_rows"
                            :columns="columns"
                            :activeCell.sync="activeCell"
                            :cellValidation.sync="cellValidation"
                            :isAppendSection="true"
                            :dataKey="dataKey"
                            :actions="actions"
                            :frozenColumnAction="frozenColumnAction"
                            :showSelectAll="rowSelectionEnabled"
                            ref="appendSectionBody"
                        >
                        </editable-table-body>

                        <editable-table-body
                            :rows="displayRows"
                            :columns="columns"
                            :activeCell.sync="activeCell"
                            :cellValidation.sync="cellValidation"
                            :dataKey="dataKey"
                            :actions="actions"
                            :showSelectAll="rowSelectionEnabled"
                            :frozenColumnAction="frozenColumnAction"
                            ref="sectionBody"
                        >
                        </editable-table-body>

                        <tbody
                            v-if="
                                showMessageIsEmpty && d_rows.length === 0 && d_new_rows.length === 0
                            "
                        >
                            <tr class="b-table-empty-row" role="row">
                                <td :colspan="columns.length + (actions.length > 0 ? 1 : 0)">
                                    <div role="alert" aria-live="polite">
                                        <div class="text-center my-2">
                                            {{ emptyMessage }}
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>

                        <editable-table-footer
                            v-if="d_footer_rows.length !== 0"
                            :rows="d_footer_rows"
                            :columns="columns"
                            :showColumnAction="actions.length !== 0 || rowSelectionEnabled"
                        >
                        </editable-table-footer>
                    </table>
                </div>
            </b-col>
        </b-row>

        <editable-paginator
            v-if="paginatorTotalRows !== 0"
            :totalRows="paginatorTotalRows"
            :perPage="paginatorPerPage"
            :pageOptions="pageOptions"
            :pageChanged="serverSidePagination ? pageChanged : onClientPageChanged"
            :perPageFunction="serverSidePagination ? perPageFunction : onClientPerPageChanged"
            :currentPage="paginatorCurrentPage"
        >
        </editable-paginator>

        <editable-bulk-edit-modal
            v-if="showBulkEdit"
            :modalId="bulkEditModalId"
            :columns="columns"
            :selectedRows="d_selected_rows"
            @apply="onBulkEditApply"
        />
    </div>
</template>

<script>
import ShowMessage from '@/components/messages/ShowMessage';
import EditableTableBody from './EditableTableBody.vue';
import EditableTableHeader from './EditableTableHeader.vue';
import EditableTableFooter from './EditableTableFooter.vue';
import EditablePaginator from './EditablePaginator.vue';
import EditableToolbar from './EditableToolbar.vue';
import EditableBulkEditModal from './EditableBulkEditModal.vue';
import Utils from '@/common/utils.js';

export default {
    name: 'MdEditableGrid',
    mixins: [ShowMessage],

    components: {
        EditableTableBody,
        EditableTableHeader,
        EditableTableFooter,
        EditablePaginator,
        EditableToolbar,
        EditableBulkEditModal
    },

    provide() {
        return {
            $parentInstance: this
        };
    },

    props: {
        showAdd: {
            type: Boolean,
            default: false
        },
        showDelete: {
            type: Boolean,
            default: false
        },
        showSelectAll: {
            type: Boolean,
            default: false
        },
        selectable: {
            type: Boolean,
            default: false
        },
        multiple: {
            type: Boolean,
            default: true
        },
        showSearch: {
            type: Boolean,
            default: false
        },
        columns: {
            type: Array,
            required: true,
            validator: value => {
                return value.every(
                    columns =>
                        typeof columns.key === 'string' &&
                        typeof columns.label === 'string' &&
                        (columns.editable === undefined || typeof columns.editable === 'boolean') &&
                        (columns.class === undefined || typeof columns.class === 'string') &&
                        (columns.dataType === undefined || typeof columns.dataType === 'string') &&
                        (columns.formatter === undefined ||
                            typeof columns.formatter === 'function') &&
                        (columns.props === undefined || typeof columns.props === 'object')
                );
            }
        },
        rows: {
            type: Array,
            required: true
        },
        footerRows: {
            type: Array,
            default: () => []
        },
        readonly: {
            type: Boolean,
            default: false
        },
        customActionButtons: {
            type: Array,
            default: () => [],
            validator: value => {
                return value.every(
                    action =>
                        (typeof action.visible === 'boolean' ||
                            typeof action.visible === 'function') &&
                        typeof action.icon === 'string' &&
                        typeof action.handler === 'function' &&
                        (action.disabled === undefined || typeof action.disabled === 'function')
                );
            }
        },
        addActionButton: {
            type: Object,
            default: () => ({
                visible: false,
                title: null,
                handler: null,
                tooltip: null
            }),
            validator: value => {
                return (
                    typeof value.visible === 'boolean' ||
                    value.handler === undefined ||
                    typeof value.handler === 'function'
                );
            }
        },
        showBulkEdit: {
            type: Boolean,
            default: false
        },
        deleteActionButton: {
            type: Object,
            default: () => ({
                visible: false,
                icon: 'fas fa-trash',
                handler: null,
                tooltip: null,
                disabled: null
            }),
            validator: value => {
                return (
                    typeof value.visible === 'boolean' ||
                    typeof value.visible === 'function' ||
                    typeof value.handler === 'function' ||
                    typeof value.disabled === 'function'
                );
            }
        },
        printActionButton: {
            type: Object,
            default: () => ({
                visible: false,
                icon: 'fas fa-print',
                handler: null,
                tooltip: null,
                disabled: null
            }),
            validator: value => {
                return (
                    typeof value.visible === 'boolean' ||
                    typeof value.visible === 'function' ||
                    typeof value.handler === 'function' ||
                    typeof value.disabled === 'function'
                );
            }
        },
        headerTitle: {
            type: String,
            default: null
        },
        headerStyle: {
            type: String,
            default: ''
        },
        perPage: {
            type: Number,
            default: 10
        },
        pageChanged: Function,
        perPageFunction: Function,
        pageOptions: {
            type: Array,
            default: () => [5, 10, 15]
        },
        totalRows: {
            type: Number,
            default: 0
        },
        currentPage: {
            type: Number,
            default: 1
        },
        showMessageIsEmpty: {
            type: Boolean,
            default: false
        },
        emptyText: {
            type: String,
            default: null
        },
        validateRowCallback: {
            type: Function,
            default: null
        },
        dataKey: {
            type: [String, Number, Function],
            default: ''
        },
        rowStyle: {
            type: Function,
            default: null
        },
        frozenColumnAction: {
            type: Boolean,
            default: false
        },
        rowValidator: {
            Type: Function,
            default: null
        },
        appendNewRows: {
            Type: Function,
            default: null
        },
        serverSidePagination: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            invalid: false,
            activeCell: null,
            d_new_rows: [],
            cellValidation: {},
            d_rows: this.rows,
            d_footer_rows: this.footerRows,
            d_selected_rows: [],
            d_selected_row_keys: [],
            d_search_text: '',
            d_applied_search: '',
            d_clientPage: 1,
            d_clientPerPage: this.perPage,
            bulkEditModalId: Utils.generateUUID()
        };
    },

    watch: {
        rows(newValue) {
            this.d_rows = newValue;
            this.cellValidation = {};
            this.activeCell = null;
            this.pruneSelection();
        },
        footerRows(newValue) {
            this.d_footer_rows = newValue;
        },
        activeCell(newValue) {
            this.cellActivate(newValue);
        }
    },

    computed: {
        showHeader() {
            return this.showAdd || this.showDelete || this.showBulkEdit || !!this.headerTitle;
        },
        actions() {
            return [
                {
                    id: 'delete',
                    visible: false,
                    icon: 'med-bin',
                    handler: this.deleteActionButton.handler,
                    disabled: this.deleteActionButton.disabled,
                    tooltip: this.$tc('Shared.DelRecord', 2),
                    ...this.deleteActionButton
                },
                {
                    id: 'print',
                    visible: false,
                    icon: 'med-print',
                    handler: this.printActionButton.handler,
                    disabled: this.printActionButton.disabled,
                    tooltip: this.$t('Shared.Report'),
                    ...this.printActionButton
                },
                ...this.customActionButtons
            ].filter(action => action.visible && typeof action.handler === 'function');
        },
        emptyMessage() {
            return this.emptyText ?? this.$t('MdTable.nRecShow');
        },
        dataToRender() {
            return this.d_new_rows.concat(this.d_rows);
        },
        editableColumns() {
            return this.columns
                .map((column, index) => ({ index, editable: column.editable }))
                .filter(column => column.editable === undefined || column.editable === true)
                .map(element => element.index);
        },
        filteredRows() {
            if (this.serverSidePagination || !this.d_applied_search.trim()) {
                return this.d_rows;
            }
            const query = this.d_applied_search.trim().toLowerCase();
            return this.d_rows.filter(row =>
                Object.values(row).some(
                    val =>
                        val !== null &&
                        val !== undefined &&
                        String(val)
                            .toLowerCase()
                            .includes(query)
                )
            );
        },
        displayRows() {
            if (this.serverSidePagination) {
                return this.d_rows;
            }
            const start = (this.d_clientPage - 1) * this.d_clientPerPage;
            return this.filteredRows.slice(start, start + this.d_clientPerPage);
        },
        paginatorTotalRows() {
            return this.serverSidePagination ? this.totalRows : this.filteredRows.length;
        },
        paginatorCurrentPage() {
            return this.serverSidePagination ? this.currentPage : this.d_clientPage;
        },
        paginatorPerPage() {
            return this.serverSidePagination ? this.perPage : this.d_clientPerPage;
        },
        allRowsSelected() {
            if (this.d_rows.length === 0) return false;
            return this.d_rows.every((item, index) =>
                this.d_selected_row_keys.includes(this.resolveRowKey(item, index))
            );
        },
        someRowsSelected() {
            return this.d_selected_row_keys.length > 0 && !this.allRowsSelected;
        },
        rowSelectionEnabled() {
            return this.showSelectAll || this.selectable;
        }
    },

    methods: {
        actionVisible(action, item) {
            if (typeof action.visible === 'function') {
                try {
                    return action.visible(item);
                } catch (e) {
                    return true;
                }
            }

            return action.visible !== false;
        },

        getColumnHeader(column) {
            const required =
                Array.isArray(column.validator) &&
                column.validator.some(rule => rule.required === true);

            if (
                Object.prototype.hasOwnProperty.call(column, 'props') &&
                Object.prototype.hasOwnProperty.call(column.props, 'frozen')
            ) {
                return {
                    label: column.label,
                    frozen: column.props.frozen ?? false,
                    alignFrozen: column.props.alignFrozen ?? 'left',
                    required
                };
            }

            return { label: column.label, required };
        },

        async saveNewRow(row, rowIndex) {
            const invalidCells = this.validateRow(this.$refs.appendSectionBody, row, rowIndex);

            if (invalidCells.length === 0) {
                const saved = await this.appendNewRows([row]);

                if (saved && rowIndex !== -1) {
                    this.d_new_rows.splice(rowIndex, 1);
                }
            } else {
                this.$refs.appendSectionBody.invalidateCells(invalidCells, rowIndex);
            }
        },

        async saveNewRows() {
            if (!this.$refs.appendSectionBody) return true;

            const rowsToSave = [];
            const rowsInvalid = [];

            this.d_new_rows.forEach((row, index) => {
                const invalidCells = this.validateRow(this.$refs.appendSectionBody, row, index);

                if (invalidCells.length === 0) {
                    rowsToSave.push(row);
                } else {
                    rowsInvalid.push({ row: row, invalidCells: invalidCells });
                }
            });

            if (rowsToSave.length !== 0 && this.appendNewRows) {
                const saved = await this.appendNewRows(rowsToSave);

                if (saved) {
                    rowsToSave.forEach(row => {
                        const rowIndex = this.d_new_rows.indexOf(row);

                        if (rowIndex !== -1) {
                            this.d_new_rows.splice(rowIndex, 1);
                        }
                    });
                }
            }

            if (rowsInvalid.length !== 0) {
                rowsInvalid.forEach(invalid => {
                    const rowIndex = this.d_new_rows.indexOf(invalid.row);

                    if (rowIndex !== -1) {
                        this.$refs.appendSectionBody.invalidateCells(
                            invalid.invalidCells,
                            rowIndex
                        );
                    }
                });

                this.showMessage(
                    this.$tc('Msg.Error'),
                    this.$t('Msg.InvalidFieldValuesDetected'),
                    'errorAutoHide'
                );
            }

            // true only when every pending row was committed successfully
            return this.d_new_rows.length === 0;
        },

        async save() {
            if (this.showAdd && this.d_new_rows.length > 0) {
                const committed = await this.saveNewRows();
                if (!committed) {
                    this.invalid = true;
                    return false;
                }
            }
            return this.validate();
        },

        validateRow(sectionRef$, row, rowIndex) {
            let invalidCells = sectionRef$.validateRow(rowIndex);

            if (this.rowValidator && invalidCells.length === 0) {
                invalidCells = this.rowValidator(row);
            }

            return invalidCells;
        },

        validate() {
            let result = true;

            //Validate new rows section
            const appendSectionBody = this.$refs.appendSectionBody;
            if (this.showAdd && appendSectionBody) {
                result = this.d_new_rows.length === 0;
                this.d_new_rows.forEach((row, index) => {
                    const invalidCells = this.validateRow(appendSectionBody, row, index);

                    if (invalidCells.length !== 0) {
                        appendSectionBody.invalidateCells(invalidCells, index);
                    }
                });
            }

            //Validate body section
            const sectionBody = this.$refs.sectionBody;
            const rowsToValidate = this.serverSidePagination ? this.d_rows : this.displayRows;
            rowsToValidate.forEach((row, index) => {
                const invalidCells = this.validateRow(sectionBody, row, index);

                if (invalidCells.length !== 0) {
                    sectionBody.invalidateCells(invalidCells, index);
                    result = false;
                }
            });

            this.invalid = !result;
            return result;
        },

        openBulkEdit() {
            this.$bvModal.show(this.bulkEditModalId);
        },

        onBulkEditApply(editedValues) {
            this.$emit('bulk-edit-complete', {
                rows: this.d_selected_rows,
                editedValues
            });
        },

        onClientPageChanged(page) {
            this.d_clientPage = page;
        },

        onClientPerPageChanged(perPage) {
            this.d_clientPerPage = perPage;
            this.d_clientPage = 1;
        },

        cellAddComplete(data) {
            this.$emit('cell-add-complete', data);
        },

        cellEditComplete(data) {
            this.$emit('cell-edit-complete', data);
        },

        cellDblClick(data) {
            this.$emit('cell-dbl-click', data);
        },

        rowClick(data) {
            this.$emit('row-click', data);
        },

        rowDblClick(data) {
            this.$emit('row-dbl-click', data);
        },

        resolveRowKey(item, index) {
            if (!this.dataKey) return `row_${index}`;
            if (typeof this.dataKey === 'function') return this.dataKey(item, index);
            return item[this.dataKey] ?? `row_${index}`;
        },

        isRowKeySelected(rowKey) {
            return this.d_selected_row_keys.includes(rowKey);
        },

        pruneSelection() {
            if (this.d_selected_row_keys.length === 0) return;

            const currentKeys = this.d_rows.map((item, index) => this.resolveRowKey(item, index));
            const keepIndexes = [];
            this.d_selected_row_keys.forEach((key, idx) => {
                if (currentKeys.includes(key)) keepIndexes.push(idx);
            });

            this.d_selected_row_keys = keepIndexes.map(idx => this.d_selected_row_keys[idx]);
            this.d_selected_rows = keepIndexes.map(idx => this.d_selected_rows[idx]);
        },

        toggleRow(rowKey, item) {
            const idx = this.d_selected_row_keys.indexOf(rowKey);
            if (idx !== -1) {
                this.d_selected_row_keys.splice(idx, 1);
                this.d_selected_rows.splice(
                    this.d_selected_rows.findIndex((r, i) => this.resolveRowKey(r, i) === rowKey),
                    1
                );
            } else {
                if (!this.multiple) {
                    this.d_selected_row_keys = [];
                    this.d_selected_rows = [];
                }
                this.d_selected_row_keys.push(rowKey);
                if (item) this.d_selected_rows.push(item);
            }
            this.$emit('select-row', this.d_selected_rows);
        },

        onToggleSelectAll(newValue) {
            // b-form-checkbox fires 'change' on reactive prop updates too, not just
            // user clicks. When the user clicks, allRowsSelected still reflects the
            // pre-click state so newValue differs. On a reactive update it already
            // matches — skip to avoid spuriously selecting all rows.
            if (newValue === this.allRowsSelected) return;
            this.toggleSelectAll();
        },

        toggleSelectAll() {
            if (this.allRowsSelected) {
                this.d_selected_row_keys = [];
                this.d_selected_rows = [];
            } else {
                this.d_selected_row_keys = this.d_rows.map((item, index) =>
                    this.resolveRowKey(item, index)
                );
                this.d_selected_rows = [...this.d_rows];
            }
            this.$emit('select-row', this.d_selected_rows);
        },

        onSearch() {
            if (this.serverSidePagination) {
                this.$emit('search', this.d_search_text);
            } else {
                this.d_applied_search = this.d_search_text;
                this.d_clientPage = 1;
            }
        },

        cellActivate(data) {
            this.$emit('cell-active', data);
        }
    }
};
</script>

<style>
.table-invalid {
    border: 2px solid red;
    border-radius: 4px;
}
.rowSelected {
    background: linear-gradient(0deg, #19bee8 3%, #d9f4fb 4%, #d9f4fb);
}
.table .table-frozen-column {
    position: sticky;
}
.table tbody tr .table-frozen-column,
.md-editable-grid tbody td {
    background: inherit !important;
}
.table th.table-frozen-column,
.table tbody td.table-frozen-column {
    z-index: 1;
}
.table tbody tr:nth-of-type(odd) {
    background-color: white;
}

.md-editable-grid .md-editable-grid-cell-editable {
    padding: 0.25rem 0rem !important;
    vertical-align: middle !important;
    min-width: 120px;
}
.md-editable-grid .md-editable-grid-cell-footer {
    font-weight: bold;
    text-align: right !important;
    background-color: white;
    text-transform: uppercase !important;
}
.md-editable-grid .md-editable-grid-cell-focus,
.md-editable-grid .table-frozen-column.md-editable-grid-cell-focus {
    background-color: transparent !important;
    background-image: linear-gradient(
        0deg,
        #19bee8 0%,
        #19bee8 4%,
        #f6f6f6 5%,
        #ffffff 50%,
        #ffffff 100%
    ) !important;
    background-repeat: no-repeat !important;
}
.md-editable-grid tbody.md-editable-grid-body-append {
    height: 48px !important;
}
.md-editable-grid tbody.md-editable-grid-body-append td.md-editable-grid-cell-focus {
    /*box-shadow: inset 0 0 0 2px green !important;
    border-radius: 6px;*/
}
.md-editable-grid td.md-editable-grid-cell-focus-invalid {
    background-color: #fae3de !important;
    box-shadow: inset 0 0 0 2px #007bff !important;
    border-radius: 6px;
}
.md-editable-grid tbody.md-editable-grid-body-append td.md-editable-grid-cell-focus-invalid {
    /*background-color: #fae3de !important;
    box-shadow: inset 0 0 0 2px green !important;
    border-radius: 6px;*/
}
.md-editable-grid td.md-editable-grid-cell-invalid {
    background-color: #fae3de !important;
    box-shadow: inset 0 0 0 2px red !important;
    border-radius: 6px;
}
.md-editable-grid td .md-editable-grid-cell-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
}
.md-editable-grid .editable-numeric-cell-spin-editing {
    border-top: 1px solid #eee;
    border-right: 0;
    border-bottom: 1px solid #ddd;
    border-left: 1px solid #eee;
}
.md-editable-grid .editable-numeric-cell-spin-editing button {
    color: #19bee8;
    padding-right: 2px;
    padding-left: 2px;
}
.md-editable-grid .editable-numeric-cell-spin-editing button:hover {
    color: #fff;
    background-color: #19bee8;
}

.md-editable-grid .editable-numeric-cell-spin-editing button:first-child {
    border-radius: 0 4px 0 0;
}

.md-editable-grid .editable-numeric-cell-spin-editing button:last-child {
    border-radius: 0 0 4px 0;
}
.md-editable-grid td .md-editable-grid-cell-display {
    flex: 1;
    padding: 8px;
    cursor: pointer;
    min-height: 16px;
}
.md-editable-grid td .md-editable-grid-cell-non-editable {
    cursor: default;
    color: #555;
}
.md-editable-grid tbody td.md-editable-grid-cell-readonly {
    background-color: #f9f9f9 !important;
}
.md-editable-grid td .md-editable-grid-cell-content {
    flex: 1;
    padding: 6px;
    outline: none;
    background-color: #fff;
    height: 100%;
    box-sizing: border-box;
}
.md-editable-grid td .md-editable-grid-cell-editing {
    flex: 1;
    padding: 6px;
    outline: none;
    width: calc(100% - 6px);
    border-top: 1px solid #eee;
    border-right: 1px solid #eee;
    border-bottom: 1px solid #ddd;
    border-left: 1px solid #eee;
    border-radius: 4px;
    background-color: #fff;
    cursor: text;
}
.md-editable-grid tbody.md-editable-grid-body-append td .md-editable-grid-cell-editing {
    /*flex: 1;
    padding: 6px;
    outline: none;
    width: calc(100% - 6px);
    border: 2px solid green;
    border-radius: 4px;
    background-color: #fff;
    cursor: text;*/
}
.md-editable-grid td .md-editable-grid-cell-content-invalid {
    flex: 1;
    padding: 6px;
    outline: none;
    width: calc(100% - 6px);
    border: 2px solid red;
    border-radius: 4px;
    background-color: #fff;
}
.md-editable-grid tbody.md-editable-grid-body-append tr:nth-of-type(even) {
    /*background-color: #9bf887;*/
}
.md-editable-grid tbody.md-editable-grid-body-append tr:nth-of-type(odd) {
    /*background-color: #97d7e7;*/
}
.md-editable-grid tbody.md-editable-grid-body-append tr:first-child {
    background: linear-gradient(0deg, #f8ffc4, white);
}
.editable-grid-subbar {
    background-color: #f6f6f6;
    padding: 2px 12px;
    border: solid 1px #e0e0e0;
    border-top: none;
    border-bottom: none;
}
</style>
